<!--
/**
 * @component PageSubscriptionCancelled
 * @description Écran affiché lorsque l'utilisateur connecté est un client Homeys dont
 *  tous les abonnements sont annulés (cf. router-guard `router.plugin.ts`).
 *
 *  Deux variantes selon le rôle :
 *  - **Owner** : message d'accueil chaleureux, badge de conservation des données 15j
 *    (CGV art. 9.1 — masqué si la fenêtre est dépassée), accès aux factures (PDF + ZIP)
 *    via `<InvoiceCard />`, lien support et déconnexion.
 *  - **Non-owner** (employé / employé read-only / administrateur) : message légal,
 *    instruction de contacter le super administrateur, badge de rétention 15j (statique),
 *    bouton "Changer d'espace entreprise" si l'utilisateur a une autre company active,
 *    et déconnexion. Pas d'accès aux factures (cohérent avec backend strict owner —
 *    `api-metier company.get.invoices.route.js`).
 */
-->
<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useAuthStore } from "@core/stores/auth.store"
import { useConfigStore } from "@core/stores/config.store"
import InvoiceCard from "@modules/espace-entreprise/components/invoice/InvoiceCard.vue"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            // Variante owner
            owner: {
                hero_title: "Heureux de vous revoir 👋",
                hero_subtitle:
                    "Votre abonnement Homeys est arrivé à son terme.",
                data_retention:
                    "Vos données et vos bâtiments sont conservés encore {days} jours. Au-delà, ils seront automatiquement supprimés.",
                invoices_title: "Vos factures",
                invoices_subtitle:
                    "Conservez l'historique de votre facturation, même après l'annulation.",
                support_text: "Besoin d'aide ?",
            },
            // Variante non-owner
            nonOwner: {
                title: "Abonnement annulé",
                text: "L'abonnement de votre entreprise a été annulé.",
                text_admin:
                    "Contactez le super administrateur de votre entreprise pour souscrire à nouveau.",
                text_retention:
                    "Les données de l'entreprise seront conservées 15 jours, puis supprimées automatiquement.",
                text_support: "Pour toute autre question, écrivez à",
                multiple_company:
                    "Vous êtes associé à plusieurs entreprises avec un abonnement actif. Vous pouvez changer d'espace entreprise pour continuer.",
                change_company: "Changer d'espace entreprise",
            },
            // NB : l'email support est volontairement absent de l'i18n. vue-i18n parse `@`
            // comme un linked-message ("@:foo") — un message contenant "support@homeys.io"
            // lève `INVALID_LINKED_FORMAT` (code 10) au runtime et plante la page (page blanche
            // observée le 2026-05-10 sur prod release v328). L'adresse est rendue en dur
            // dans le template (cf. <a href="mailto:…">).
            logout: "Se déconnecter",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const authStore = useAuthStore()
const configStore = useConfigStore()
const router = useRouter()

// ================================
// STATE
// ================================
/** Chargement pendant la déconnexion */
const logoutLoading = ref(false)

// ================================
// COMPUTED
// ================================
/** Distingue la variante visuelle. Le backend (`api-metier`) restreint aussi les factures à `owner`. */
const isOwner = computed(() => authStore.isCompanyOwner)

/**
 * Message complet (déjà interpolé) à afficher dans le badge de rétention.
 * Pré-calculé ici (et non `t(...)` inline dans le template) pour passer une **valeur**
 * et non un `ComputedRef` au compileur i18n — un Ref-circular planterait au runtime prod.
 * `null` si la fenêtre 15j est dépassée ou si la donnée est manquante → badge masqué.
 */
const retentionBadgeText = computed<string | null>(() => {
    const days = authStore.daysUntilDataDeletion
    if (typeof days !== "number" || days <= 0) return null
    return t("owner.data_retention", { days })
})

/**
 * Affiche le bloc "Changer d'espace entreprise" pour les non-owners qui ont une autre
 * company active — évite de bloquer un utilisateur multi-company.
 */
const showOtherActiveCompany = computed(() => authStore.hasOtherActiveCompany)

/** Titre du scope (marque) pour l'en-tête auth. */
const getScopeTitle = computed(
    () => configStore.getScopeBrand || configStore.getScopeName || "Homeys"
)

// ================================
// INTERACTIONS
// ================================
/** Déconnexion et retour au login : navigation d'abord (route /login autorisée côté garde), puis reset des stores (sauf config). */
async function onLogout(): Promise<void> {
    logoutLoading.value = true
    try {
        await router.push({ name: "PageLogin" })
    } catch {
        // Navigation annulée : on déconnecte quand même ci-dessous.
    } finally {
        authStore.logout()
        logoutLoading.value = false
    }
}

/** Redirection vers la connexion multi-entreprise. */
async function onChangeCompanySpace(): Promise<void> {
    await router.push({
        path: "/login",
        query: {
            view: "multi-company",
            username: authStore.username,
            user: authStore.userId,
        },
    })
}
</script>

<template>
    <!-- ================================ -->
    <!-- VARIANTE OWNER -->
    <!-- ================================ -->
    <div
        v-if="isOwner"
        class="page-subscription-cancelled page-subscription-cancelled--owner min-h-screen bg-gradient-to-b from-primary-50 to-surface-50 py-10 px-4"
    >
        <div class="max-w-2xl mx-auto flex flex-col gap-6">
            <!-- HERO -->
            <BaseCard>
                <template #content>
                    <div
                        class="flex flex-col items-center gap-3 px-4 py-6 text-center"
                    >
                        <div
                            class="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center"
                        >
                            <i
                                class="pi pi-heart-fill text-3xl text-primary-600"
                            />
                        </div>
                        <h1
                            class="text-2xl md:text-3xl font-bold text-primary-900"
                        >
                            {{ t("owner.hero_title") }}
                        </h1>
                        <p class="text-primary-800 font-medium">
                            {{ t("owner.hero_subtitle") }}
                        </p>
                        <div
                            v-if="retentionBadgeText"
                            class="flex items-center gap-2 text-sm text-orange-700 bg-orange-50 px-3 py-2 rounded-md mt-1"
                        >
                            <i class="pi pi-clock" />
                            <span>{{ retentionBadgeText }}</span>
                        </div>
                    </div>
                </template>
            </BaseCard>

            <!-- FACTURES (réservé owner — cf. backend) -->
            <div>
                <div class="px-2 mb-3">
                    <h2 class="text-lg font-semibold text-primary-900 mb-1">
                        {{ t("owner.invoices_title") }}
                    </h2>
                    <p class="text-sm text-primary-700">
                        {{ t("owner.invoices_subtitle") }}
                    </p>
                </div>
                <InvoiceCard />
            </div>

            <!-- FOOTER -->
            <div
                class="flex flex-col md:flex-row items-center justify-between gap-3 mt-2 px-2 text-sm text-primary-700"
            >
                <p>
                    {{ t("owner.support_text") }}
                    <a
                        class="text-primary-600 underline ml-1"
                        href="mailto:support@homeys.io"
                        >support@homeys.io</a
                    >
                </p>
                <BaseButton
                    severity="secondary"
                    text
                    :label="t('logout')"
                    icon="pi pi-sign-out"
                    :loading="logoutLoading"
                    @click="onLogout"
                />
            </div>
        </div>
    </div>

    <!-- ================================ -->
    <!-- VARIANTE NON-OWNER -->
    <!-- ================================ -->
    <div
        v-else
        class="page-subscription-cancelled page-subscription-cancelled--non-owner page flex justify-center items-center min-h-screen p-4"
    >
        <div class="w-full max-w-2xl">
            <BaseCard class="page-subscription-cancelled__card">
                <template #title>
                    <FormAuthHeader
                        :title="getScopeTitle"
                        :subtitle="t('nonOwner.title')"
                    />
                </template>
                <template #content>
                    <div class="flex flex-col gap-8 w-full py-4">
                        <div
                            class="flex flex-col gap-3 text-center text-sm text-primary-800"
                        >
                            <p class="text-balance">
                                {{ t("nonOwner.text") }}
                            </p>
                            <p class="text-balance font-medium">
                                {{ t("nonOwner.text_admin") }}
                            </p>
                            <p
                                class="flex items-center justify-center gap-2 text-orange-700 bg-orange-50 px-3 py-2 rounded-md text-balance"
                            >
                                <i class="pi pi-clock" />
                                <span>{{ t("nonOwner.text_retention") }}</span>
                            </p>
                            <p class="text-balance text-primary-700 mt-1">
                                {{ t("nonOwner.text_support") }}
                                <a
                                    class="text-primary-600 underline"
                                    href="mailto:support@homeys.io"
                                    >support@homeys.io</a
                                >.
                            </p>
                        </div>
                        <BaseButton
                            class="w-full"
                            :label="t('logout')"
                            icon="pi pi-sign-out"
                            :loading="logoutLoading"
                            @click="onLogout"
                        />
                        <div
                            v-if="showOtherActiveCompany"
                            class="flex flex-col gap-4 items-center"
                        >
                            <p
                                class="text-sm text-primary-800 text-center text-balance"
                            >
                                {{ t("nonOwner.multiple_company") }}
                            </p>
                            <BaseButton
                                severity="secondary"
                                outlined
                                class="w-full sm:w-auto"
                                :label="t('nonOwner.change_company')"
                                @click="onChangeCompanySpace"
                            />
                        </div>
                        <FooterLogin :forget-password="true" />
                    </div>
                </template>
            </BaseCard>
        </div>
    </div>
</template>
